<template>
  <card-to-mobile-layout title="Temporary Housing Request Host Information">
    <div class="row">
      <base-textfield
        :model-value="hostBuilding?.buildingName"
        name="building"
        label="Building"
        div-class="col-12 col-md-6 mb-4"
        disabled
      />
      <base-auto-complete
        v-model="room"
        label="Room # where you will be staying"
        name="room"
        option-label="name"
        option-value="id"
        :suggestions="hostingRooms"
        div-class="col-12 col-md-6 mb-4"
        force-selection
        :help-text="`Example: ${hostBuilding.prefix}###`"
        :rules="rules.room"
        :form-name="formName"
        @complete="search"
        @item-select="lookupHosts"
      />
    </div>
    <h3>Hosts</h3>
    <p v-if="!hostsSearched">Find a room first</p>
    <template v-else-if="hosts.length">
      <div v-for="(host, i) in hosts" :key="host.studentId" class="row">
        <base-textfield
          :model-value="host.name"
          :name="`host${i}`"
          label="Name"
          div-class="col-12 col-md-6 mb-4"
          disabled
        />
        <base-textfield
          v-model="host.phone"
          :name="`hostPhone${i}`"
          label="Phone"
          div-class="col-12 col-md-6 mb-4"
        />
      </div>
    </template>
    <p v-else>Empty Room</p>
    <div class="row">
      <base-text-area
        v-model="requestReason"
        name="reason"
        label="Reason for requesting temporary housing"
        div-class="col-12"
        :rules="rules.requestedReason"
        :form-name="formName"
      />
    </div>
  </card-to-mobile-layout>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { computed, ref } from "vue";

import { holidayLeaveResponses } from "@/api";

import { Rules } from "../useValidations";

type Props = {
  roomId: number;
  hosts: { studentId: number; name: string; phone: string }[];
  requestReason: string;
  rules: Rules;
  formName: string;
  hostBuilding: {
    buildingId: number;
    buildingName: string;
    prefix: string;
  };
};

const props = defineProps<Props>();

type Emits = {
  (e: "update:roomId", payload: Props["roomId"]): void;
  (e: "update:hosts", payload: Props["hosts"]): void;
  (e: "update:requestReason", payload: Props["requestReason"]): void;
};

const emit = defineEmits<Emits>();

const roomId = computed({
  get() {
    return props.roomId;
  },
  set(v: number) {
    emit("update:roomId", v);
  },
});
const hosts = computed({
  get() {
    return props.hosts;
  },
  set(v: Props["hosts"]) {
    emit("update:hosts", v);
  },
});
const requestReason = computed({
  get() {
    return props.requestReason;
  },
  set(v: Props["requestReason"]) {
    emit("update:requestReason", v);
  },
});

const room = ref();
const hostingRooms = ref([]);
const hostsSearched = ref(false);

const search = debounce(async ({ query: room }) => {
  hostingRooms.value = await holidayLeaveResponses.hostingRooms({
    query: {
      roomName: room,
      buildingId: props.hostBuilding.buildingId,
    },
  });
}, 250);

const lookupHosts = async () => {
  hostsSearched.value = true;
  hosts.value = await holidayLeaveResponses.hosts({
    query: {
      roomId: room.value.id,
    },
  });
  roomId.value = room.value.id;
};
</script>
